import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { EVENT_TYPE_PERMANENT } from '../../constants/crm-constants';
import { getDateAsUtc } from '../../core/functions/date-functions';
import { DirtyStatus } from '../../model/common/dirty-status';
import { MOCEvent, MOCEventConnection } from '../../model/crm/moc-event';
import { MOCEventAckInput } from '../../model/crm/moc-event-ack-input';
import { MOCEventAcknowledgement } from '../../model/crm/moc-event-acknowledgement';
import { MOCEventAssociatedCategory } from '../../model/crm/moc-event-associated-category';
import { MOCEventAssociatedCategoryInput } from '../../model/crm/moc-event-associated-category-input';
import { MOCEventAOR } from '../../model/crm/moc-event-aor';
import { MOCEventAORInput } from '../../model/crm/moc-event-aor-input';
import { MOCEventEntry } from '../../model/crm/moc-event-entry';
import { MOCEventEntryInput } from '../../model/crm/moc-event-entry-input';
import { MOCEventInput } from '../../model/crm/moc-event-input';
import { MOCEventSearch } from '../../model/crm/moc-event-search';
import { format } from 'date-fns';
import { getMostRecentMOCEventsInList } from '../../crm/functions/moc-event-functions';
import {
  getCorrectedDate,
  isNullOrUndefined,
  sortByProperty,
} from '../../utils/utils';
import { SapOutage } from '../../model/sap/sap-outage';
import { SelectItem } from '@openng/optimus-ui/api';
import {
  MOCEventCategory,
  MOCEventCategoryConnection,
} from 'src/app/model/crm/moc-event-category';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { MOCEventType } from 'src/app/model/crm/moc-event-type';
import { BusinessUnit } from 'src/app/model/admin/business-unit';

@Injectable({
  providedIn: 'root',
})
export class MocEventBuilderService {
  constructor() {}

  buildNullMocEvent(): MOCEvent {
    return {
      id: null,
      title: null,
      MOCEventType: null,
      eventStatus: null,
      MOCEventAssociatedCategories: null,
      startDt: null,
      endDt: null,
      document: null,
      description: null,
      MOCEventEntries: [],
      MOCEventAORs: [],
      MOCEventAcknowledgements: [],
      ShiftGroup: null,
      parentMocId: null,
      historyId: null,
      createdAt: null,
      createdBy: null,
    } as unknown as MOCEvent;
  }

  buildMOCEventForm(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      title: [null, Validators.required],
      eventStatus: [{ value: null, disabled: true }, Validators.required],
      eventType: [null, Validators.required],
      eventCategories: [null, Validators.required],
      startDt: [null, Validators.required],
      endDt: [{ value: null, disabled: true }],
      aors: [null, Validators.required],
      document: null,
      description: [null, Validators.required],
      notes: null,
      createdBy: { value: null, disabled: true },
      createdAt: { value: null, disabled: true },
      sapOutageNumber: null,
      version: null,
    });
  }

  buildMOCAcknowledgementForm(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      controller: [{ value: null, disabled: true }],
      completedDt: [{ value: null, disabled: true }],
      acknowledged: [{ value: null, disabled: true }],
      comments: [null],
    });
  }

  buildMOCEvent(item: any): MOCEvent {
    const builtCategories = this.buildMOCEventAssociatedCategories(
      item.MOCEventAssociatedCategories
    );
    const isOutage = builtCategories.some(
      (assocCat) =>
        assocCat.MOCEventCategory?.id === '77b367e2-036f-436a-980d-69804d470875'
    );
    return {
      id: item.id,
      title: item.title,
      MOCEventType: item.MOCEventType
        ? {
            id: item.MOCEventType.id,
            name: item.MOCEventType.name,
            enabled: item.MOCEventType.enabled,
          }
        : null,
      eventStatus: null,
      MOCEventAssociatedCategories: builtCategories,
      startDt: isOutage
        ? new Date(item.startDt)
        : getCorrectedDate(item.startDt)!,
      endDt: isOutage ? new Date(item.endDt) : getCorrectedDate(item.endDt)!,
      document: item.document,
      description: item.description,
      MOCEventEntries: this.buildMOCEventEntries(item.MOCEventEntries),
      MOCEventAORs: this.buildMocEventAORs(item.MOCEventAORs),
      MOCEventAcknowledgements: this.buildMocEventAcknowledgements(
        item.MOCEventAcknowledgements
      ),
      ShiftGroup: item.ShiftGroup
        ? {
            id: item.ShiftGroup.id,
            name: item.ShiftGroup.name,
          }
        : null,
      parentMocId: item.parentMocId,
      historyId: item.historyId,
      createdAt: new Date(getDateAsUtc(item.createdAt)),
      sapOutageNumber: item.sapOutageNumber,
      createdBy: item.createdBy,
      version: item.version,
      dirtyStatus: DirtyStatus.UNMODIFIED,
    };
  }

  buildMOCEventAssociatedCategories(
    items: any[]
  ): MOCEventAssociatedCategory[] {
    if (!items) {
      return [];
    }

    const retVal: MOCEventAssociatedCategory[] = [];
    items
      .sort((a, b) => b.createdAt - a.createdAt)
      .forEach((i) => {
        retVal.push({
          id: i.id,
          MOCEventCategory: i.MOCEventCategory
            ? {
                id: i.MOCEventCategory.id,
                name: i.MOCEventCategory.name,
                ShiftGroup: i.MOCEventCategory.ShiftGroup
                  ? {
                      id: i.MOCEventCategory.ShiftGroup.id,
                      name: i.MOCEventCategory.ShiftGroup.name,
                    }
                  : null,
                enabled: i.MOCEventCategory.enabled,
                createdBy: i.MOCEventCategory.createdBy,
                createdAt: new Date(getDateAsUtc(i.MOCEventCategory.createdAt)),
                deletedAt: i.MOCEventCategory.deletedAt,
              }
            : null,
        });
      });

    return retVal;
  }

  buildMOCEventEntries(items: any[]): MOCEventEntry[] {
    if (!items) {
      return [];
    }

    const retVal: MOCEventEntry[] = [];
    items
      .sort((a, b) => b.createdAt - a.createdAt)
      .forEach((i) => {
        retVal.push({
          id: i.id,
          note: i.note,
          user: i.createdBy,
          time: new Date(getDateAsUtc(i.createdAt)),
        });
      });

    return retVal;
  }

  buildMocEventAORs(items: any[]): MOCEventAOR[] {
    if (!items) {
      return [];
    }

    const retVal: MOCEventAOR[] = [];
    items.forEach((i) => {
      retVal.push({
        id: i.id,
        AOR: i.AOR
          ? {
              id: i.AOR.id,
              name: i.AOR.name,
            }
          : null,
      });
    });

    return retVal;
  }

  buildMocEventAcknowledgements(items: any[]): MOCEventAcknowledgement[] {
    if (!items) {
      return [];
    }

    const retVal: MOCEventAcknowledgement[] = [];
    items.forEach((i) => {
      retVal.push({
        id: i.id,
        mocEventId: i.mocEventId,
        contactId: i.contactId,
        Contact: i.Contact,
        controller: i.Contact.firstName + ' ' + i.Contact.lastName,
        acknowledged: i.acknowledged,
        comments: i.comments,
        completedDt: i.completedDt
          ? format(getDateAsUtc(i.completedDt), 'yyyy-MM-dd hh:mm:ss a')
          : null,
        createdBy: i.createdBy,
        createdAt: new Date(getDateAsUtc(i.createdAt)),
        updater: i.updater,
        updatedAt: i.updatedAt,
        deletedAt: i.deletedAt,
        dirtyStatus: i.dirtyStatus,
        version: i.version,
      });
    });

    return retVal;
  }

  buildMOCEventNewInput(
    data: any,
    isPermanent: SelectItem,
    shiftGroupId: string
  ): MOCEventInput {
    const retVal: MOCEventInput = {
      title: data.title,
      eventTypeId: data.eventType,
      MOCEventAssociatedCategories: [],
      startDt: data.startDt.toISOString(),
      endDt: !isPermanent
        ? data.endDt.toISOString()
        : new Date('01/01/2200').toISOString(),
      document: data.document,
      description: data.description,
      sapOutageNumber: data.sapOutageNumber,
      shiftGroupId: shiftGroupId,
      MOCEventEntries: [],
      MOCEventAORs: [],
      dirtyStatus: DirtyStatus.NEW,
      expectedVersion: 0,
      historyId: 0,
      parentMocId: undefined,
    };

    if (data.eventCategories) {
      data.eventCategories.forEach((category: any) => {
        const ec: MOCEventAssociatedCategoryInput = {
          eventCategoryId: category,
        };
        retVal.MOCEventAssociatedCategories?.push(ec);
      });

      if (
        data.eventCategories.some(
          (category: any) => category === '77b367e2-036f-436a-980d-69804d470875'
        )
      ) {
        const offset = new Date().getTimezoneOffset();
        const start = new Date(data.startDt);
        start.setMinutes(start.getMinutes() - offset);
        if (!isPermanent) {
          const end = new Date(data.endDt);
          end.setMinutes(end.getMinutes() - offset);
          retVal.endDt = end.toISOString();
        }
        retVal.startDt = start.toISOString();
      }
    }

    if (data.aors) {
      data.aors.forEach((aor: any) => {
        const ed: MOCEventAORInput = {
          aorId: aor,
        };
        retVal.MOCEventAORs?.push(ed);
      });
    }

    if (data.notes) {
      data.notes.forEach((note: any) => {
        const en: MOCEventEntryInput = {
          note: note.note,
          time: note.time?.toISOString(),
          user: note.user,
        };
        retVal.MOCEventEntries?.push(en);
      });
    }

    return retVal;
  }

  buildMOCEventVersionInput(
    data: any,
    isPermanent: SelectItem,
    shiftGroupId: string,
    oldMocEvent: MOCEvent,
    manualClose = false
  ): MOCEventInput {
    const containsOutageCategory = (data.eventCategories as string[]).some(
      (category) => category === '77b367e2-036f-436a-980d-69804d470875'
    );

    let startDt: Date = data.startDt;
    let endDt: Date = data.endDt;

    if (containsOutageCategory) {
      // If we have the outage category, we need to send the current local time to the DB, pretending its in UTC
      // Because we want it to show up correctly in the UI instead of adding a few hours everytime
      let offset = new Date().getTimezoneOffset();
      startDt.setMinutes(startDt.getMinutes() - offset);
      endDt.setMinutes(endDt.getMinutes() - offset);
    }

    const retVal: MOCEventInput = {
      title: data.title,
      eventTypeId: data.eventType,
      MOCEventAssociatedCategories: [],
      startDt: startDt.toISOString(),
      endDt: manualClose
        ? endDt.toISOString()
        : !isPermanent
          ? endDt.toISOString()
          : new Date('01/01/2200').toISOString(),
      document: data.document,
      description: data.description,
      sapOutageNumber: data.sapOutageNumber,
      shiftGroupId: shiftGroupId,
      MOCEventEntries: [],
      MOCEventAORs: [],
      dirtyStatus: DirtyStatus.NEW,
      expectedVersion: 0,
      parentMocId: oldMocEvent.parentMocId
        ? oldMocEvent.parentMocId
        : oldMocEvent.id!,
      historyId: oldMocEvent.historyId + 1,
    };

    if (data.eventCategories) {
      data.eventCategories.forEach((category: any) => {
        const ec: MOCEventAssociatedCategoryInput = {
          eventCategoryId: category,
        };
        retVal.MOCEventAssociatedCategories?.push(ec);
      });
    }

    if (data.notes) {
      data.notes.forEach((note: any) => {
        const en: MOCEventEntryInput = {
          note: note.note,
          time: note.time?.toISOString(),
          user: note.user,
        };
        retVal.MOCEventEntries?.push(en);
      });
    }

    if (data.aors) {
      data.aors.forEach((aor: any) => {
        const ed: MOCEventAORInput = {
          aorId: aor,
        };
        retVal.MOCEventAORs?.push(ed);
      });
    }
    return retVal;
  }

  buildMOCEventAcknowledgementInput(
    mocEventId: string,
    contactId: string,
    existingAck?: MOCEventAcknowledgement
  ): MOCEventAckInput {
    return {
      id: existingAck ? existingAck.id : '',
      mocEventId: mocEventId,
      contactId: contactId,
      comments: existingAck ? existingAck.comments : undefined,
      acknowledged: true,
      expectedVersion: existingAck ? existingAck.version! : 0,
    };
  }

  buildMocEventAcknowledgement(acknowledgement: any): MOCEventAcknowledgement {
    const retVal: MOCEventAcknowledgement = {
      id: acknowledgement.id,
      mocEventId: acknowledgement.mocEventId,
      contactId: acknowledgement.contactId,
      Contact: acknowledgement.Contact,
      controller:
        acknowledgement.Contact.firstName +
        ' ' +
        acknowledgement.Contact.lastName,
      acknowledged: acknowledgement.acknowledged,
      comments: acknowledgement.comments,
      completedDt: acknowledgement.completedDt
        ? format(
            getDateAsUtc(acknowledgement.completedDt),
            'yyyy-MM-dd hh:mm:ss a'
          )
        : null,
      createdBy: acknowledgement.createdBy,
      createdAt: new Date(getDateAsUtc(acknowledgement.createdAt)),
      updater: acknowledgement.updater,
      updatedAt: acknowledgement.updatedAt,
      deletedAt: acknowledgement.deletedAt,
      dirtyStatus: acknowledgement.dirtyStatus,
      version: acknowledgement.version,
    };

    return retVal;
  }

  buildMocEventsForAOR(
    data: MOCEvent[],
    incomingControllerId: String,
    turnoverDate: Date
  ): { unacknowledged: MOCEvent[]; acknowledged: MOCEvent[] } {
    const recentMOCEvents = getMostRecentMOCEventsInList(data);
    const unacknowledgedList: MOCEvent[] = [];
    if (!isNullOrUndefined(recentMOCEvents)) {
      recentMOCEvents.forEach((event) => {
        const startDt = getCorrectedDate(event.startDt)!;
        const endDt = getCorrectedDate(event.endDt)!;
        const mocCreatedAt = getCorrectedDate(event.createdAt)!;
        const today = new Date();
        const turnoverDateClone = new Date(turnoverDate);
        turnoverDateClone.setHours(23);
        turnoverDateClone.setMinutes(59);
        turnoverDateClone.setSeconds(59);
        const acknow = event.MOCEventAcknowledgements.find(
          (mea) => mea.Contact.id === incomingControllerId
        );
        if (
          // We only care about MOCs that existed on or before the turnover date,
          // If they were created after we don't want to show them in the event that
          // a turnover is returned to and completed on a later date
          mocCreatedAt <= turnoverDateClone &&
          startDt <= today &&
          endDt >= today &&
          (isNullOrUndefined(acknow) ||
            (!isNullOrUndefined(acknow) && acknow?.acknowledged === false))
        ) {
          unacknowledgedList.push(event);
        }
      });
    }

    const acknowAndUpcomingList: MOCEvent[] = [];
    if (!isNullOrUndefined(recentMOCEvents)) {
      turnoverDate.setDate(turnoverDate.getDate() + 7);
      recentMOCEvents.forEach((event) => {
        const startDt = getCorrectedDate(event.startDt)!;
        const endDt = getCorrectedDate(event.endDt)!;
        const today = new Date();
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const acknow = event.MOCEventAcknowledgements.find(
          (mea) =>
            mea.Contact.id === incomingControllerId && mea.acknowledged === true
        );
        if (
          (((startDt <= today && endDt >= today) ||
            event.MOCEventType?.name === EVENT_TYPE_PERMANENT) &&
            !isNullOrUndefined(acknow)) ||
          (startDt >= today && startDt <= turnoverDate)
        ) {
          acknowAndUpcomingList.push(event);
        }
      });
    }

    return {
      unacknowledged: unacknowledgedList,
      acknowledged: acknowAndUpcomingList,
    };
  }

  /**
   * Historical turnovers shouldn't show if acknowledgements have
   * been reset or any new MOC Events for the district.
   * @param data
   * @param incomingCompletedDt
   */
  buildHistoricalAcknowledgements(data: MOCEvent[], incomingCompletedDt: Date) {
    let retVal: MOCEvent[] = [];
    const recentMOCEvents = getMostRecentMOCEventsInList(data);
    if (!isNullOrUndefined(data)) {
      data.forEach((event) => {
        const createdAt = new Date(getDateAsUtc(event.createdAt));
        if (incomingCompletedDt >= createdAt) {
          if (
            !isNullOrUndefined(recentMOCEvents.find((e) => e.id === event.id))
          ) {
            retVal.push(event);
          } else {
            event.title = event.title + ' - V' + event.historyId;
            retVal.push(event);
          }
        }
      });
    }

    retVal.sort((a, b) => sortByProperty(a, b, 'title'));

    retVal = getMostRecentMOCEventsInList(retVal);

    return retVal;
  }

  buildMOCEventSearchItems(items: any): MOCEventSearch[] {
    if (!items) {
      return [];
    }

    const retVal: MOCEventSearch[] = [];
    items.forEach((i: any) => {
      retVal.push({
        id: i.id,
        title: i.title,
        eventType: i.eventType,
        eventStatus: i.eventStatus,
        eventCategories: i.eventCategories,
        aors: i.aors,
        startDt: i.eventCategories.includes('Outage')
          ? i.startDt
          : getCorrectedDate(i.startDt),
        endDt: i.eventCategories.includes('Outage')
          ? i.endDt
          : getCorrectedDate(i.endDt),
        description: i.description,
        sapOutageNumber: i.sapOutageNumber,
        document: i.document,
        acknowledged: i.acknowledged,
        createdAt: new Date(getDateAsUtc(i.createdAt)),
      });
    });

    return retVal;
  }

  buildMOCEventFromSapOutage(outage: SapOutage) {
    const mocEvent = this.buildNullMocEvent();
    // Map fields from the outage onto the moc
    mocEvent.title = outage.shortDesc;
    mocEvent.MOCEventType = {
      id: 'bc05ac76-e0e3-11ea-a0fa-06e2fd5611f4',
      name: 'Scheduled',
      enabled: true,
    };
    mocEvent.startDt = new Date(outage.reqStart);
    mocEvent.endDt = new Date(outage.reqEnd);

    mocEvent.MOCEventAssociatedCategories = [
      {
        id: '',
        MOCEventCategory: {
          id: '77b367e2-036f-436a-980d-69804d470875',
          name: 'Outage',
          ShiftGroup: null,
          // these fields don't actually matter for the build, we just need it for typing
          enabled: true,
          createdAt: new Date(),
          createdBy: 'system',
        },
      },
    ];
    if (outage.Facility) {
      mocEvent.MOCEventAORs = outage.Facility.AORs.map((aor) => {
        return {
          id: '',
          AOR: {
            id: aor.id,
            name: aor.name,
          },
        };
      });
    }

    const entries: MOCEventEntry[] = outage.Tasks.filter(
      (task) => task.taskText || task.longText
    ).map((task) => {
      let mocNote = `${task.taskText ? task.taskText : ''}`;
      if (task.longText) {
        mocNote += task.longText;
      }

      return {
        id: '',
        note: mocNote,
        user: 'system',
        time: new Date(),
      };
    });
    mocEvent.MOCEventEntries = entries;
    mocEvent.sapOutageNumber = outage.notifNum;
    mocEvent.description = outage.longDesc;

    return mocEvent;
  }

  buildShiftGroup(item: any): ShiftGroup {
    return {
      id: item?.id,
      name: item?.name,
    };
  }

  buildMOCEventCategory(item: any): MOCEventCategory {
    return {
      id: item.id,
      name: item.name,
      enabled: item.enabled,
      createdBy: item.createdBy,
      createdAt: getCorrectedDate(item.createdAt)!,
      ShiftGroup: this.buildShiftGroup(item.ShiftGroup),
      businessUnits: item.businessUnits,
      businessUnitString:
        item.businessUnits?.map((i: BusinessUnit) => i.name)?.join(', ') ?? '',
    };
  }

  buildMOCEventType(item: any): MOCEventType {
    return {
      id: item.id,
      name: item.name,
      enabled: item.enabled,
    };
  }

  buildMOCEventConnection(item: any): MOCEventConnection {
    return {
      items: this.buildMOCEventSearchItems(item.items),
      totalRecords: item.totalRecords,
    };
  }

  buildMOCEventCategoryConnection(item: any): MOCEventCategoryConnection {
    return {
      items: item.items.map((i: any) => this.buildMOCEventCategory(i)),
      totalRecords: item.totalRecords,
    };
  }
}
